import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { PaginatedResponse, PlayEvent, PlayEventSeries } from '@court-sync/shared';
import {
  Badge,
  Button,
  Input,
  PaginatedTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { playEventService } from '@/services/playEvent';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_EVENT_STATUS_BADGE_VARIANT,
  PLAY_EVENT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { ArrowLeft, ChevronRight, Search } from 'lucide-react';

const PAGE_SIZE = 20;

function isPaginatedResponse(
  res: { success: boolean; data?: PlayEvent[]; pagination?: PaginationState }
): res is PaginatedResponse<PlayEvent> {
  return res.success && res.pagination != null;
}

function formatEventDate(event: PlayEvent): string {
  const start = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  return format(start, 'EEE d MMM yyyy');
}

function formatEventTimeRange(event: PlayEvent): string {
  const start = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  const end = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  return `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
}

interface PlayEventSeriesSessionsPanelProps {
  series: PlayEventSeries;
  onBack: () => void;
}

export function PlayEventSeriesSessionsPanel({ series, onBack }: PlayEventSeriesSessionsPanelProps) {
  const navigate = useNavigate();
  const { currentClub } = useCurrentClub();
  const [rows, setRows] = useState<PlayEvent[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlayEvent['status'] | 'all'>('all');

  const loadPage = useCallback(
    async (nextPage: number) => {
      if (!currentClub) return;
      setLoading(true);
      try {
        const res = await playEventService.listEvents(currentClub.organizationId, {
          page: nextPage,
          limit: PAGE_SIZE,
          seriesId: series.id,
          allUpcoming: true,
          startsAtOrder: 'asc',
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(searchQuery ? { search: searchQuery } : {}),
        });
        if (isPaginatedResponse(res)) {
          setRows(res.data ?? []);
          setPagination(res.pagination);
        } else {
          setRows([]);
          setPagination(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentClub, searchQuery, series.id, statusFilter]
  );

  useEffect(() => {
    void loadPage(page);
  }, [loadPage, page]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  const openEvent = useCallback(
    (event: PlayEvent) => {
      navigate(`/admin/play-events/${event.id}`);
    },
    [navigate]
  );

  const columns: PaginatedTableColumn<PlayEvent>[] = useMemo(
    () => [
      {
        key: 'when',
        header: 'When',
        className: 'whitespace-nowrap',
        render: (event) => (
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {formatEventDate(event)}
            </p>
            <p className="text-xs text-muted-foreground">{formatEventTimeRange(event)}</p>
          </div>
        ),
      },
      {
        key: 'format',
        header: 'Format',
        render: (event) => (
          <span className="text-sm text-muted-foreground">
            {PLAY_EVENT_FORMAT_LABELS[event.format]} · {PLAY_EVENT_SIDE_LABELS[event.side]}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (event) => (
          <Badge variant={PLAY_EVENT_STATUS_BADGE_VARIANT[event.status]}>
            {PLAY_EVENT_STATUS_LABELS[event.status]}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-10 text-right',
        render: () => (
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden />
        ),
      },
    ],
    []
  );

  const totalCount = pagination?.total ?? rows.length;

  return (
    <DashboardDataCard
      toolbar={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Button type="button" variant="ghost" size="sm" className="-ml-2 w-fit" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
              All series
            </Button>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading sessions…' : `${totalCount} session${totalCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Search sessions…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                aria-label="Search series sessions"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as PlayEvent['status'] | 'all')}
            >
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(PLAY_EVENT_STATUS_LABELS) as PlayEvent['status'][]).map((status) => (
                  <SelectItem key={status} value={status}>
                    {PLAY_EVENT_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      }
    >
      <PaginatedTable
        columns={columns}
        data={rows}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onRowClick={openEvent}
        emptyMessage={
          searchQuery || statusFilter !== 'all'
            ? 'No sessions match your filters.'
            : 'No sessions scheduled for this series yet.'
        }
        noWrapperPadding
        mobileTitleColumnKey="when"
      />
    </DashboardDataCard>
  );
}
